import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Checkbox } from '@/components/ui/checkbox';
import { BookOpen, Upload, Video, FileText, Trash2, Users, Play, Edit } from 'lucide-react';
import ProfessionalPDFViewer from '../ProfessionalPDFViewer';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Course Categories with bilingual labels
const COURSE_CATEGORIES = {
  therapeutic: {
    labelAr: 'الدورات العلاجية',
    labelEn: 'Therapeutic Courses',
    types: {
      ABA: { ar: 'العلاج السلوكي التطبيقي', en: 'ABA' },
      OT: { ar: 'العلاج الوظيفي', en: 'Occupational Therapy' },
      ST: { ar: 'علاج النطق', en: 'Speech Therapy' }
    }
  },
  supporting: {
    labelAr: 'الدورات الداعمة',
    labelEn: 'Supporting Courses',
    types: {
      Safety: { ar: 'السلامة', en: 'Safety' },
      ChildProtection: { ar: 'حماية الطفل', en: 'Child Protection' },
      FirstAid: { ar: 'الإسعافات الأولية', en: 'First Aid' }
    }
  },
  administrative: {
    labelAr: 'الدورات الإدارية والمهنية',
    labelEn: 'Administrative & Professional',
    types: {
      Quality: { ar: 'الجودة', en: 'Quality' },
      Ethics: { ar: 'أخلاقيات العمل', en: 'Work Ethics' },
      CenterMgmt: { ar: 'إدارة مراكز التوحد', en: 'Autism Center Management' },
      Documentation: { ar: 'التوثيق والتقارير', en: 'Documentation & Reports' }
    }
  }
};

// Legacy types (for backwards compatibility)
const COURSE_TYPES = {
  ABAT: 'ABAT',
  'QASP-S': 'QASP-S',
  QBA: 'QBA'
};

const CoursesManagement = () => {
  const { t, i18n } = useTranslation();
  const isArabic = i18n.language === 'ar';
  const [contents, setContents] = useState([]);
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showUploadDialog, setShowUploadDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showStudentsDialog, setShowStudentsDialog] = useState(false);
  const [showViewDialog, setShowViewDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [selectedContent, setSelectedContent] = useState(null);
  const [editForm, setEditForm] = useState({ title: '', description: '', newFile: null });
  const [selectedCategory, setSelectedCategory] = useState('therapeutic');
  const [selectedCourseType, setSelectedCourseType] = useState('ABA');
  const [uploadForm, setUploadForm] = useState({
    title: '',
    description: '',
    file_type: 'powerpoint',
    file: null,
    category: 'therapeutic',
    course_type: 'ABA'
  });
  const [selectedStudents, setSelectedStudents] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadStatus, setUploadStatus] = useState('');
  const uploadCancelTokenRef = useRef(null);

  useEffect(() => {
    fetchContents();
    fetchStudents();
  }, []);

  const fetchContents = async () => {
    try {
      const response = await axios.get(`${API}/teacher/courses`);
      setContents(response.data.contents);
    } catch (error) {
      toast.error(t('error'));
    } finally {
      setLoading(false);
    }
  };

  const fetchStudents = async () => {
    try {
      const response = await axios.get(`${API}/teacher/students`);
      setStudents(response.data.students);
    } catch (error) {
      console.error('Error fetching students:', error);
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    
    if (!file) {
      return;
    }
    
    // Check file size (max 1GB)
    const maxSize = 1024 * 1024 * 1024; // 1GB in bytes
    if (file.size > maxSize) {
      toast.error(`حجم الملف كبير جداً. الحد الأقصى هو 1 غيغابايت. حجم الملف الحالي: ${(file.size / (1024 * 1024)).toFixed(2)} MB`);
      e.target.value = null; // Clear input
      return;
    }
    
    // Validate file type
    const ext = file.name.split('.').pop().toLowerCase();
    if (uploadForm.file_type === 'powerpoint' && !['ppt', 'pptx', 'pdf'].includes(ext)) {
      toast.error('يرجى اختيار ملف عرض تقديمي (.pdf أو .pptx)');
      e.target.value = null;
      return;
    }
    
    if (uploadForm.file_type === 'video' && !['mp4', 'webm', 'mov'].includes(ext)) {
      toast.error('يرجى اختيار ملف فيديو (.mp4, .webm, أو .mov)');
      e.target.value = null;
      return;
    }
    
    // Warn about PPTX files - recommend PDF
    if (['ppt', 'pptx'].includes(ext)) {
      toast.warning('💡 ملاحظة: يُفضل رفع ملفات PDF للحصول على أفضل توافق. قد لا يعمل تحويل PowerPoint في بعض البيئات.', { duration: 6000 });
    }
    
    console.log('✅ File selected:', file.name, `(${(file.size / (1024 * 1024)).toFixed(2)} MB)`);
    setUploadForm({ ...uploadForm, file: file });
  };

  const handleUpload = async () => {
    if (!uploadForm.title || !uploadForm.description || !uploadForm.file) {
      toast.error(t('fillAllFields') || 'يرجى ملء جميع الحقول');
      return;
    }

    if (!uploadForm.category || !uploadForm.course_type) {
      toast.error(t('selectCategory') || 'يرجى اختيار التصنيف ونوع الدورة');
      return;
    }

    const file = uploadForm.file;
    const fileSizeMB = (file.size / (1024 * 1024)).toFixed(2);
    
    console.log('📤 Starting upload...');
    console.log('File:', file.name);
    console.log('Size:', fileSizeMB, 'MB');
    console.log('Type:', file.type);
    console.log('Category:', uploadForm.category);
    console.log('Course Type:', uploadForm.course_type);

    setUploading(true);
    setUploadProgress(0);
    setUploadStatus('جاري التحضير...');

    const formData = new FormData();
    formData.append('title', uploadForm.title);
    formData.append('description', uploadForm.description);
    formData.append('category', uploadForm.category);
    formData.append('course_type', uploadForm.course_type);
    formData.append('file_type', uploadForm.file_type);
    formData.append('file', file);

    try {
      setUploadStatus('جاري الرفع...');
      
      // Create cancel token
      const CancelToken = axios.CancelToken;
      const source = CancelToken.source();
      uploadCancelTokenRef.current = source;
      
      const response = await axios.post(`${API}/teacher/courses/upload`, formData, {
        headers: { 
          'Content-Type': 'multipart/form-data'
        },
        timeout: 1800000, // 30 minutes timeout for very large files
        cancelToken: source.token,
        onUploadProgress: (progressEvent) => {
          const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          setUploadProgress(percentCompleted);
          
          const uploadedMB = (progressEvent.loaded / (1024 * 1024)).toFixed(2);
          const speedMBps = progressEvent.rate ? (progressEvent.rate / (1024 * 1024)).toFixed(2) : '0';
          setUploadStatus(`جاري الرفع: ${uploadedMB} / ${fileSizeMB} MB (${percentCompleted}%) - ${speedMBps} MB/s`);
          
          console.log(`Upload progress: ${percentCompleted}%`);
        }
      });
      
      console.log('✅ Upload complete');
      
      // For PowerPoint files, start polling for conversion status
      if (uploadForm.file_type === 'powerpoint') {
        const contentId = response.data?.content_id;
        if (contentId) {
          toast.success(t('contentUploadedConversion') || 'تم رفع المحتوى بنجاح! جاري تحويل PowerPoint في الخلفية...');
          // Start polling for conversion status
          pollConversionStatus(contentId);
        } else {
          toast.success(t('contentUploaded') || 'تم رفع المحتوى بنجاح');
        }
      } else {
        toast.success(t('contentUploaded') || 'تم رفع المحتوى بنجاح');
      }
      setShowUploadDialog(false);
      setUploadForm({ title: '', description: '', file_type: 'powerpoint', file: null, category: 'therapeutic', course_type: 'ABA' });
      setUploadProgress(0);
      setUploadStatus('');
      fetchContents();
    } catch (error) {
      console.error('❌ Upload error:', error);
      
      let errorMessage = 'حدث خطأ في الرفع';
      
      if (axios.isCancel(error)) {
        console.log('Upload cancelled by user');
        errorMessage = 'تم إلغاء الرفع';
      } else if (error.code === 'ECONNABORTED') {
        errorMessage = 'انتهت مهلة الرفع (30 دقيقة). الملف كبير جداً أو الاتصال بطيء.';
      } else if (error.response) {
        if (error.response.status === 413) {
          errorMessage = 'الملف كبير جداً. الحد الأقصى هو 1 غيغابايت.';
        } else if (error.response.status === 400) {
          errorMessage = error.response.data?.detail || 'بيانات غير صحيحة';
        } else {
          errorMessage = error.response.data?.detail || 'خطأ في الخادم';
        }
      } else if (error.request) {
        errorMessage = 'لا يوجد اتصال بالخادم. تحقق من الإنترنت.';
      }
      
      toast.error(errorMessage);
      setUploadStatus(axios.isCancel(error) ? 'تم الإلغاء' : 'فشل الرفع');
    } finally {
      setUploading(false);
      uploadCancelTokenRef.current = null;
    }
  };
  
  const cancelUpload = () => {
    if (uploadCancelTokenRef.current) {
      console.log('🛑 Cancelling upload...');
      uploadCancelTokenRef.current.cancel('تم إلغاء الرفع من قبل المستخدم');
    }
  };

  const pollConversionStatus = async (contentId, maxAttempts = 60) => {
    // Poll every 2 seconds for up to 2 minutes
    let attempts = 0;
    
    const checkStatus = async () => {
      try {
        const response = await axios.get(`${API}/teacher/courses/${contentId}/status`);
        const { conversion_status, is_ready, conversion_error } = response.data;
        
        console.log(`🔄 Conversion status: ${conversion_status}`);
        
        if (conversion_status === 'completed' && is_ready) {
          console.log('✅ Conversion completed successfully');
          toast.success('تم تحويل PowerPoint إلى PDF بنجاح!');
          fetchContents(); // Refresh the list
          return true; // Stop polling
        } else if (conversion_status === 'failed') {
          console.error('❌ Conversion failed:', conversion_error);
          toast.error(`فشل تحويل PowerPoint: ${conversion_error || 'خطأ غير معروف'}`);
          fetchContents(); // Refresh to show error status
          return true; // Stop polling
        } else if (attempts >= maxAttempts) {
          console.warn('⏱️ Polling timeout - conversion taking too long');
          toast.warning('التحويل يستغرق وقتاً أطول من المتوقع. تحقق من القائمة لاحقاً.');
          fetchContents();
          return true; // Stop polling
        }
        
        // Continue polling
        attempts++;
        setTimeout(checkStatus, 2000); // Check again in 2 seconds
      } catch (error) {
        console.error('Error checking conversion status:', error);
        // Don't show error to user - just stop polling
        return true;
      }
    };
    
    // Start polling
    setTimeout(checkStatus, 2000); // First check after 2 seconds
  };

  const openStudentsDialog = async (content) => {
    setSelectedContent(content);
    
    try {
      const response = await axios.get(`${API}/teacher/courses/${content.id}/students`);
      const studentEmails = response.data.student_emails;
      setSelectedStudents(studentEmails);
      setShowStudentsDialog(true);
    } catch (error) {
      toast.error(t('error'));
    }
  };

  const handleGrantAccess = async () => {
    try {
      await axios.post(`${API}/teacher/courses/${selectedContent.id}/grant-access`, {
        student_emails: selectedStudents
      });
      
      toast.success(t('accessGranted') || 'Access granted successfully');
      setShowStudentsDialog(false);
      fetchContents();
    } catch (error) {
      toast.error(t('error'));
    }
  };

  const toggleStudent = (email) => {
    if (selectedStudents.includes(email)) {
      setSelectedStudents(selectedStudents.filter(e => e !== email));
    } else {
      setSelectedStudents([...selectedStudents, email]);
    }
  };

  const openEditDialog = (content) => {
    setSelectedContent(content);
    setEditForm({ title: content.title, description: content.description, newFile: null });
    setShowEditDialog(true);
  };

  const handleUpdateContent = async () => {
    if (!editForm.title || !editForm.description) {
      toast.error(t('fillAllFields') || 'Please fill all fields');
      return;
    }

    const formData = new FormData();
    formData.append('title', editForm.title);
    formData.append('description', editForm.description);
    
    // Add new file if provided
    if (editForm.newFile) {
      formData.append('file', editForm.newFile);
      formData.append('replace_file', 'true');
    }

    try {
      await axios.put(`${API}/teacher/courses/${selectedContent.id}`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      toast.success(t('contentUpdated') || 'Content updated successfully');
      setShowEditDialog(false);
      fetchContents();
    } catch (error) {
      toast.error(t('error'));
    }
  };

  const handleDeleteContent = async () => {
    try {
      await axios.delete(`${API}/teacher/courses/${selectedContent.id}`);
      toast.success(t('contentDeleted') || 'Content deleted successfully');
      setShowDeleteDialog(false);
      fetchContents();
    } catch (error) {
      toast.error(t('error'));
    }
  };

  const viewContent = (content) => {
    setSelectedContent(content);
    setShowViewDialog(true);
  };

  const getContentsByCourseType = (type) => {
    return contents.filter(c => c.course_type === type);
  };

  const renderContentCard = (content) => (
    <Card key={content.id} className="shadow-lg hover:shadow-xl transition-shadow">
      <CardHeader className="bg-gradient-to-r from-blue-50 to-cyan-50">
        <CardTitle className="flex items-center gap-2 text-lg">
          {content.file_type === 'video' ? (
            <Video className="h-5 w-5 text-blue-600" />
          ) : (
            <FileText className="h-5 w-5 text-green-600" />
          )}
          {content.title}
          {content.file_type === 'powerpoint' && content.conversion_status !== 'completed' && (
            <span className={`text-xs px-2 py-1 rounded-full ${
              content.conversion_status === 'processing' ? 'bg-yellow-100 text-yellow-800' :
              content.conversion_status === 'failed' ? 'bg-red-100 text-red-800' :
              'bg-blue-100 text-blue-800'
            }`}>
              {content.conversion_status === 'processing' ? '🔄 جاري التحويل' :
               content.conversion_status === 'failed' ? '❌ فشل التحويل' :
               '⏳ في الانتظار'}
            </span>
          )}
          {content.file_type === 'pdf' && (
            <span className="text-xs px-2 py-1 rounded-full bg-green-100 text-green-800">
              ✅ جاهز
            </span>
          )}
        </CardTitle>
      </CardHeader>
      <CardContent className="pt-4">
        {content.conversion_status === 'failed' && (
          <div className="mb-3 p-2 bg-red-50 border border-red-200 rounded text-xs text-red-700">
            ⚠️ فشل تحويل PowerPoint. يرجى حذف هذا المحتوى وإعادة رفعه كملف PDF.
          </div>
        )}
        <p className="text-sm text-gray-600 mb-4 line-clamp-2">{content.description}</p>
        
        <div className="flex items-center gap-2 text-sm text-gray-500 mb-4">
          <Users className="h-4 w-4" />
          <span>{content.students_count || 0} {t('students')}</span>
        </div>

        <div className="flex gap-2">
          <Button
            onClick={() => viewContent(content)}
            className="flex-1 bg-gradient-to-r from-blue-600 to-cyan-600"
            size="sm"
          >
            <Play className="h-4 w-4 mr-2" />
            {t('view')}
          </Button>
          
          <Button
            onClick={() => openEditDialog(content)}
            variant="outline"
            size="sm"
          >
            <Edit className="h-4 w-4" />
          </Button>
          
          <Button
            onClick={() => openStudentsDialog(content)}
            variant="outline"
            size="sm"
          >
            <Users className="h-4 w-4" />
          </Button>
          
          <Button
            onClick={() => {
              setSelectedContent(content);
              setShowDeleteDialog(true);
            }}
            variant="destructive"
            size="sm"
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
      </CardContent>
    </Card>
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  // Filter contents by category and type
  const getContentsByCategory = (category, type) => {
    return contents.filter(c => c.category === category && c.course_type === type);
  };

  // Get all contents for a category
  const getAllContentsByCategory = (category) => {
    return contents.filter(c => c.category === category);
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-3xl font-bold text-gray-900 flex items-center gap-2">
            <BookOpen className="h-8 w-8" />
            {t('courses') || 'الدورات'}
          </h2>
          <p className="text-gray-600 mt-1">{t('manageCourseContent') || 'إدارة محتوى الدورات'}</p>
        </div>
        
        <Button
          onClick={() => {
            setUploadForm({ 
              title: '', 
              description: '', 
              file_type: 'powerpoint', 
              file: null,
              category: selectedCategory,
              course_type: selectedCourseType
            });
            setShowUploadDialog(true);
          }}
          className="bg-gradient-to-r from-indigo-600 to-cyan-600"
        >
          <Upload className="h-4 w-4 mr-2" />
          {t('uploadContent') || 'رفع محتوى'}
        </Button>
      </div>

      {/* Category Tabs */}
      <Tabs defaultValue="therapeutic" onValueChange={(val) => {
        setSelectedCategory(val);
        // Set first course type of the category
        const firstType = Object.keys(COURSE_CATEGORIES[val].types)[0];
        setSelectedCourseType(firstType);
      }}>
        <TabsList className="grid w-full grid-cols-3 h-auto">
          {Object.entries(COURSE_CATEGORIES).map(([key, cat]) => (
            <TabsTrigger key={key} value={key} className="flex flex-col py-2">
              <span className="text-sm font-bold">{isArabic ? cat.labelAr : cat.labelEn}</span>
            </TabsTrigger>
          ))}
        </TabsList>

        {Object.entries(COURSE_CATEGORIES).map(([catKey, category]) => (
          <TabsContent key={catKey} value={catKey} className="mt-6">
            {/* Course Type Sub-tabs */}
            <Tabs defaultValue={Object.keys(category.types)[0]} onValueChange={setSelectedCourseType}>
              <TabsList className="flex flex-wrap gap-2 h-auto bg-gray-100 p-2 rounded-lg">
                {Object.entries(category.types).map(([typeKey, typeLabel]) => (
                  <TabsTrigger 
                    key={typeKey} 
                    value={typeKey}
                    className="data-[state=active]:bg-gradient-to-r data-[state=active]:from-indigo-600 data-[state=active]:to-cyan-600 data-[state=active]:text-white"
                  >
                    {isArabic ? typeLabel.ar : typeLabel.en}
                  </TabsTrigger>
                ))}
              </TabsList>

              {Object.keys(category.types).map((typeKey) => (
                <TabsContent key={typeKey} value={typeKey} className="mt-4">
                  {getContentsByCategory(catKey, typeKey).length === 0 ? (
                    <Card>
                      <CardContent className="pt-6 text-center text-gray-500">
                        <p>{t('noContent') || 'لا يوجد محتوى'}</p>
                        <p className="text-sm mt-2">{t('uploadContentHint') || 'اضغط على "رفع محتوى" لإضافة دورة جديدة'}</p>
                      </CardContent>
                    </Card>
                  ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                      {getContentsByCategory(catKey, typeKey).map(renderContentCard)}
                    </div>
                  )}
                </TabsContent>
              ))}
            </Tabs>
          </TabsContent>
        ))}
      </Tabs>

      {/* Upload Dialog */}
      <Dialog open={showUploadDialog} onOpenChange={setShowUploadDialog}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{t('uploadContent') || 'رفع محتوى'}</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            {/* Category Selection */}
            <div>
              <Label>{t('category') || 'التصنيف'}</Label>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-2 mt-2">
                {Object.entries(COURSE_CATEGORIES).map(([key, cat]) => (
                  <button
                    key={key}
                    type="button"
                    onClick={() => {
                      setUploadForm({ 
                        ...uploadForm, 
                        category: key,
                        course_type: Object.keys(cat.types)[0]
                      });
                    }}
                    className={`p-3 rounded-lg border-2 text-sm font-medium transition-all ${
                      uploadForm.category === key 
                        ? 'border-indigo-500 bg-indigo-50 text-indigo-700' 
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <div>{isArabic ? cat.labelAr : cat.labelEn}</div>
                  </button>
                ))}
              </div>
            </div>

            {/* Course Type Selection */}
            {uploadForm.category && (
              <div>
                <Label>{t('courseType') || 'نوع الدورة'}</Label>
                <div className="flex flex-wrap gap-2 mt-2">
                  {Object.entries(COURSE_CATEGORIES[uploadForm.category]?.types || {}).map(([key, label]) => (
                    <button
                      key={key}
                      type="button"
                      onClick={() => setUploadForm({ ...uploadForm, course_type: key })}
                      className={`px-4 py-2 rounded-lg border text-sm transition-all ${
                        uploadForm.course_type === key 
                          ? 'bg-indigo-600 text-white border-indigo-600' 
                          : 'border-gray-300 hover:border-indigo-400'
                      }`}
                    >
                      {isArabic ? label.ar : label.en}
                    </button>
                  ))}
                </div>
              </div>
            )}

            <div>
              <Label>{t('title')}</Label>
              <Input
                value={uploadForm.title}
                onChange={(e) => setUploadForm({ ...uploadForm, title: e.target.value })}
                placeholder={t('enterTitle') || 'أدخل العنوان'}
              />
            </div>

            <div>
              <Label>{t('description')}</Label>
              <Textarea
                value={uploadForm.description}
                onChange={(e) => setUploadForm({ ...uploadForm, description: e.target.value })}
                placeholder={t('enterDescription') || 'أدخل الوصف'}
                rows={3}
              />
            </div>

            <div>
              <Label>{t('fileType') || 'نوع الملف'}</Label>
              <div className="flex flex-wrap gap-4 mt-2">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    value="powerpoint"
                    checked={uploadForm.file_type === 'powerpoint'}
                    onChange={(e) => setUploadForm({ ...uploadForm, file_type: e.target.value })}
                  />
                  <FileText className="h-4 w-4" />
                  عرض تقديمي (PDF/PPTX)
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    value="video"
                    checked={uploadForm.file_type === 'video'}
                    onChange={(e) => setUploadForm({ ...uploadForm, file_type: e.target.value })}
                  />
                  <Video className="h-4 w-4" />
                  فيديو (MP4)
                </label>
              </div>
              {uploadForm.file_type === 'powerpoint' && (
                <p className="text-xs text-amber-600 mt-2 p-2 bg-amber-50 rounded">
                  💡 يُفضل رفع ملفات PDF مباشرة للحصول على أفضل توافق. إذا كان لديك ملف PowerPoint، قم بتحويله إلى PDF أولاً.
                </p>
              )}
            </div>

            <div>
              <Label>{t('file')}</Label>
              <Input
                type="file"
                accept={uploadForm.file_type === 'powerpoint' ? '.pdf,.ppt,.pptx' : '.mp4,.webm,.mov'}
                onChange={handleFileChange}
              />
              {uploadForm.file && (
                <div className="mt-2 p-3 bg-green-50 border border-green-200 rounded-lg">
                  <p className="text-sm font-medium text-green-800">
                    ✅ {uploadForm.file.name}
                  </p>
                  <p className="text-xs text-green-600 mt-1">
                    {(uploadForm.file.size / (1024 * 1024)).toFixed(2)} MB
                    {uploadForm.file.size > 50 * 1024 * 1024 && ' - ملف كبير، قد يستغرق الرفع بضع دقائق'}
                  </p>
                </div>
              )}
              <p className="text-xs text-gray-500 mt-2">
                💡 الحد الأقصى: 1 غيغابايت (1024 ميغابايت). للحصول على أفضل أداء، استخدم ملفات أصغر من 200 ميغابايت.
              </p>
            </div>

            {/* Upload Progress */}
            {uploading && (
              <div className="space-y-3 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-blue-800 font-medium">{uploadStatus}</span>
                  <span className="font-bold text-blue-900">{uploadProgress}%</span>
                </div>
                <div className="w-full bg-blue-200 rounded-full h-3">
                  <div 
                    className="bg-gradient-to-r from-indigo-600 to-cyan-600 h-3 rounded-full transition-all duration-300 flex items-center justify-end"
                    style={{ width: `${uploadProgress}%` }}
                  >
                    <span className="text-xs text-white font-bold mr-2">{uploadProgress > 5 && `${uploadProgress}%`}</span>
                  </div>
                </div>
                <Button
                  onClick={cancelUpload}
                  variant="outline"
                  size="sm"
                  className="w-full border-red-300 text-red-600 hover:bg-red-50"
                >
                  ❌ إلغاء الرفع
                </Button>
              </div>
            )}

            <div className="flex gap-2 pt-4">
              <Button
                onClick={handleUpload}
                disabled={uploading}
                className="flex-1 bg-gradient-to-r from-indigo-600 to-cyan-600"
              >
                {uploading ? (
                  <>
                    <Upload className="h-4 w-4 mr-2 animate-pulse" />
                    {t('uploading') || 'جاري الرفع...'}
                  </>
                ) : (
                  <>
                    <Upload className="h-4 w-4 mr-2" />
                    {t('upload') || 'رفع'}
                  </>
                )}
              </Button>
              <Button
                onClick={() => setShowUploadDialog(false)}
                variant="outline"
                className="flex-1"
                disabled={uploading}
              >
                {t('cancel')}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Students Selection Dialog */}
      <Dialog open={showStudentsDialog} onOpenChange={setShowStudentsDialog}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{t('selectStudents') || 'اختيار الطلاب'}</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <p className="text-sm text-gray-600">
              {t('selectStudentsDescription') || 'اختر الطلاب المسموح لهم بالوصول إلى هذا المحتوى'}
            </p>

            <div className="space-y-2">
              {students.map((student) => (
                <div
                  key={student.email}
                  className="flex items-center gap-3 p-3 border rounded-lg hover:bg-gray-50 cursor-pointer"
                  onClick={() => toggleStudent(student.email)}
                >
                  <Checkbox
                    checked={selectedStudents.includes(student.email)}
                    onCheckedChange={() => toggleStudent(student.email)}
                  />
                  <div className="flex-1">
                    <p className="font-medium">{student.name}</p>
                    <p className="text-sm text-gray-500">{student.email}</p>
                  </div>
                </div>
              ))}
            </div>

            <div className="flex gap-2 pt-4">
              <Button
                onClick={handleGrantAccess}
                className="flex-1 bg-gradient-to-r from-indigo-600 to-cyan-600"
              >
                {t('grantAccess') || 'منح الصلاحية'}
              </Button>
              <Button
                onClick={() => setShowStudentsDialog(false)}
                variant="outline"
                className="flex-1"
              >
                {t('cancel')}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit Content Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{t('editContent') || 'تعديل المحتوى'}</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <div>
              <Label>{t('title')}</Label>
              <Input
                value={editForm.title}
                onChange={(e) => setEditForm({ ...editForm, title: e.target.value })}
                placeholder={t('enterTitle') || 'أدخل العنوان'}
              />
            </div>

            <div>
              <Label>{t('description')}</Label>
              <Textarea
                value={editForm.description}
                onChange={(e) => setEditForm({ ...editForm, description: e.target.value })}
                placeholder={t('enterDescription') || 'أدخل الوصف'}
                rows={3}
              />
            </div>

            {/* File Replacement Section */}
            <div className="border-t pt-4 mt-4">
              <Label className="text-base font-semibold mb-2 block">
                {t('replaceFile') || 'استبدال المحتوى'}
              </Label>
              <p className="text-sm text-gray-600 mb-3">
                {t('replaceFileDescription') || 'ارفع ملف جديد لاستبدال المحتوى الحالي (اختياري)'}
              </p>
              
              {selectedContent && (
                <div className="bg-gray-50 p-3 rounded-lg mb-3">
                  <p className="text-sm font-medium text-gray-700">
                    {t('currentFile') || 'الملف الحالي'}:
                  </p>
                  <p className="text-sm text-gray-600 flex items-center gap-2 mt-1">
                    {selectedContent.file_type === 'video' ? (
                      <Video className="h-4 w-4" />
                    ) : (
                      <FileText className="h-4 w-4" />
                    )}
                    {selectedContent.file_path}
                  </p>
                </div>
              )}

              <Input
                type="file"
                accept={selectedContent?.file_type === 'powerpoint' ? '.ppt,.pptx' : '.mp4,.webm,.mov'}
                onChange={(e) => setEditForm({ ...editForm, newFile: e.target.files[0] })}
              />
              
              {editForm.newFile && (
                <div className="mt-2 bg-green-50 border border-green-200 rounded p-2">
                  <p className="text-sm text-green-800 flex items-center gap-2">
                    <Upload className="h-4 w-4" />
                    {t('newFileSelected') || 'تم اختيار ملف جديد'}: {editForm.newFile.name}
                  </p>
                </div>
              )}
            </div>

            <div className="flex gap-2 pt-4">
              <Button
                onClick={handleUpdateContent}
                className="flex-1 bg-gradient-to-r from-indigo-600 to-cyan-600"
              >
                {t('save')}
              </Button>
              <Button
                onClick={() => setShowEditDialog(false)}
                variant="outline"
                className="flex-1"
              >
                {t('cancel')}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* View Content Dialog */}
      <Dialog open={showViewDialog} onOpenChange={setShowViewDialog}>
        <DialogContent className="max-w-[100vw] w-[100vw] h-[100vh] max-h-[100vh] p-0 gap-0 overflow-hidden m-0 rounded-none flex flex-col">
          <DialogHeader className="px-4 py-2 border-b flex-shrink-0 bg-white">
            <DialogTitle className="text-base font-semibold">
              {selectedContent?.course_type} - {selectedContent?.title}
            </DialogTitle>
          </DialogHeader>
          
          {selectedContent && (
            <div className="flex-1 overflow-hidden" style={{ height: 'calc(100vh - 48px)' }}>
              {selectedContent.file_type === 'video' ? (
                <div className="w-full h-full bg-black flex items-center justify-center">
                  <video
                    controls
                    className="max-w-full max-h-full"
                    src={`${API}/courses/stream/${selectedContent.id}?token=${localStorage.getItem('token')}`}
                  >
                    Your browser does not support video playback.
                  </video>
                </div>
              ) : (
                <ProfessionalPDFViewer 
                  fileUrl={`${API}/courses/stream-pdf/${selectedContent.id}?token=${localStorage.getItem('token')}`}
                  canDownload={true}
                />
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('confirmDelete')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('deleteContentConfirmation') || 'هل أنت متأكد من حذف هذا المحتوى؟ سيتم حذف الملف وإزالة الصلاحيات من جميع الطلاب.'}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('cancel')}</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteContent}
              className="bg-red-600 hover:bg-red-700"
            >
              {t('delete')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default CoursesManagement;
